import { useEffect, useMemo, useState } from "react";
import { InvoiceMailAttachmentPreview } from "@/components/invoiceMail/InvoiceMailAttachmentPreview";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  approvalStatusClass,
  approvalStatusLabel,
  decideInvoiceApproval,
  eventActionLabel,
  formatDay,
  formatIban,
  formatMoney,
  getInvoiceApproval,
  matchMethodLabel,
  saveInvoiceApprovalHeader,
  type InvoiceApprovalApprover,
  type InvoiceApprovalDetail,
  type InvoiceApprovalHeaderInput,
} from "@/lib/invoiceApproval";
import type { InvoiceMailAttachmentSummary } from "@/lib/invoiceMail";
import { toast } from "@/lib/toast";

interface HeaderForm {
  invoice_number: string;
  invoice_date: string;
  supplier_name: string;
  total_net: string;
  total_gross: string;
  currency: string;
  due_date: string;
  supplier_iban: string;
  payment_terms: string;
}

function emptyForm(): HeaderForm {
  return {
    invoice_number: "",
    invoice_date: "",
    supplier_name: "",
    total_net: "",
    total_gross: "",
    currency: "EUR",
    due_date: "",
    supplier_iban: "",
    payment_terms: "",
  };
}

function formFromDetail(detail: InvoiceApprovalDetail): HeaderForm {
  const header = detail.header;
  return {
    invoice_number: header.invoice_number || "",
    invoice_date: (header.invoice_date || "").slice(0, 10),
    supplier_name: header.supplier_name || "",
    total_net: header.total_net == null ? "" : String(header.total_net),
    total_gross: header.total_gross == null ? "" : String(header.total_gross),
    currency: header.currency || "EUR",
    due_date: (header.due_date || "").slice(0, 10),
    supplier_iban: header.supplier_iban || "",
    payment_terms: header.payment_terms || "",
  };
}

function payloadFromForm(form: HeaderForm): InvoiceApprovalHeaderInput {
  const number = (value: string) => (value.trim() === "" ? null : Number(value));
  return {
    invoice_number: form.invoice_number.trim() || null,
    invoice_date: form.invoice_date || null,
    supplier_name: form.supplier_name.trim() || null,
    total_net: number(form.total_net),
    total_gross: number(form.total_gross),
    currency: form.currency.trim() || "EUR",
    due_date: form.due_date || null,
    supplier_iban: form.supplier_iban.trim() || null,
    payment_terms: form.payment_terms.trim() || null,
  };
}

function priceLabel(state: string, percent: number | null | undefined): string {
  if (state === "higher") return `+${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(percent || 0)} % zum EK`;
  if (state === "lower") return `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(percent || 0)} % zum EK`;
  if (state === "match") return "EK stimmt";
  if (state === "no_ek") return "kein EK im Stamm";
  return "kein Stammartikel";
}

export function ApprovalDialog({
  candidateId,
  approvers,
  onClose,
  onChanged,
}: {
  candidateId: number | null;
  approvers: InvoiceApprovalApprover[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<InvoiceApprovalDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<HeaderForm>(emptyForm);
  const [note, setNote] = useState("");
  const [secondLogin, setSecondLogin] = useState("");
  const [ackDeviation, setAckDeviation] = useState(false);
  const [ackDuplicate, setAckDuplicate] = useState(false);
  const [ackProject, setAckProject] = useState(false);
  const [attachmentId, setAttachmentId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    if (candidateId == null) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNote("");
    setAckDeviation(false);
    setAckDuplicate(false);
    setAckProject(false);
    void getInvoiceApproval(candidateId)
      .then((next) => {
        if (cancelled) return;
        setDetail(next);
        setForm(formFromDetail(next));
        setSecondLogin(next.approval.second_assignee_login || "");
        const first =
          next.attachments.find((item) => (item.content_type || "").includes("pdf") || (item.name || "").toLowerCase().endsWith(".pdf")) ||
          next.attachments[0];
        setAttachmentId(first?.id || null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : "Die Rechnung konnte nicht geladen werden.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId]);

  const attachment = useMemo<InvoiceMailAttachmentSummary | null>(() => {
    const found = detail?.attachments.find((item) => item.id === attachmentId) || detail?.attachments[0];
    if (!found?.id) return null;
    return { id: found.id, name: found.name, contentType: found.content_type, size: found.size };
  }, [attachmentId, detail]);

  const gross = form.total_gross.trim() === "" ? null : Number(form.total_gross);
  const needsSecond =
    Boolean(detail) &&
    !detail?.permissions.as_second &&
    detail?.approval.status !== "warte_zweite_freigabe" &&
    gross != null &&
    gross >= (detail?.four_eyes_threshold || 5000);
  const canRelease =
    Boolean(detail?.permissions.can_decide) &&
    gross != null &&
    !Number.isNaN(gross) &&
    (detail?.price_deviation_count ? ackDeviation : true) &&
    (detail?.duplicates.length ? ackDuplicate : true) &&
    (detail?.missing_project ? ackProject : true) &&
    (needsSecond ? Boolean(secondLogin) : true);

  function patch<K extends keyof HeaderForm>(key: K, value: HeaderForm[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function run(
    key: string,
    task: () => Promise<InvoiceApprovalDetail>,
    success: string,
  ) {
    setBusy(key);
    try {
      const next = await task();
      setDetail(next);
      setForm(formFromDetail(next));
      setNote("");
      toast.success(success);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  const currency = form.currency || "EUR";
  const inputClass = "w-full rounded border border-separator bg-card px-2 py-1 text-sm";

  return (
    <Dialog
      open={candidateId != null}
      onClose={onClose}
      size="4xl"
      fullScreenMobile
      title={detail?.header.supplier_name || detail?.subject || "Rechnung"}
      description={detail ? approvalStatusLabel(detail.approval.status) : "Rechnungsfreigabe"}
      panelClassName="max-h-[92vh]"
      bodyClassName="overflow-y-auto"
      footer={
        detail?.permissions.can_decide ? (
          <>
            <Button
              variant="secondary"
              disabled={busy != null || note.trim().length < 3}
              onClick={() =>
                void run(
                  "klaerung",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "in_klaerung",
                      note,
                      header: payloadFromForm(form),
                    }),
                  "Rechnung ist in Klärung.",
                )
              }
            >
              In Klärung
            </Button>
            <Button
              variant="danger"
              disabled={busy != null || note.trim().length < 3}
              onClick={() =>
                void run(
                  "ablehnen",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "ablehnen",
                      note,
                      header: payloadFromForm(form),
                    }),
                  "Rechnung abgelehnt.",
                )
              }
            >
              Ablehnen
            </Button>
            <Button
              disabled={busy != null || !canRelease}
              onClick={() =>
                void run(
                  "frei",
                  () =>
                    decideInvoiceApproval(detail.candidate_id, {
                      action: "freigeben",
                      note: note.trim() || null,
                      second_assignee_login: needsSecond ? secondLogin : null,
                      acknowledge_deviations: ackDeviation,
                      acknowledge_duplicate: ackDuplicate,
                      acknowledge_missing_project: ackProject,
                      header: payloadFromForm(form),
                    }),
                  detail.permissions.as_second || !needsSecond
                    ? "Rechnung freigegeben."
                    : "Erste Freigabe gespeichert. Die zweite Person sieht die Rechnung unter Zweite Freigabe.",
                )
              }
            >
              {detail.permissions.as_second ? "Endgültig freigeben" : needsSecond ? "Erste Freigabe" : "Freigeben"}
            </Button>
          </>
        ) : null
      }
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error">{error}</Alert>
      ) : detail ? (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(18rem,0.85fr)]">
          <div className="min-w-0">
            {detail.attachments.length > 1 ? (
              <label className="mb-2 block text-xs text-label-secondary">
                Beleg
                <select
                  className="ml-2 rounded border border-separator bg-card px-2 py-1 text-sm"
                  value={attachment?.id || ""}
                  onChange={(event) => setAttachmentId(event.target.value)}
                >
                  {detail.attachments.map((item) => (
                    <option key={item.id || item.name} value={item.id || ""}>
                      {item.name || "Anhang"}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {attachment ? (
              <InvoiceMailAttachmentPreview candidateId={detail.candidate_id} attachment={attachment} />
            ) : (
              <div className="flex min-h-64 items-center justify-center rounded-xl border border-separator text-sm text-label-secondary">
                Kein Belegbild vorhanden.
              </div>
            )}
          </div>

          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${approvalStatusClass(detail.approval.status)}`}>
                {approvalStatusLabel(detail.approval.status)}
              </span>
              <span className="text-label-secondary">
                bei {detail.approval.assignee_name || "—"}
                {detail.approval.second_assignee_name ? ` · 2. ${detail.approval.second_assignee_name}` : ""}
              </span>
            </div>

            {detail.permissions.as_substitute ? (
              <Alert variant="info">Sie handeln als Vertretung, die Rechnung ist einer anderen Person zugewiesen.</Alert>
            ) : null}
            {detail.permissions.reason ? <Alert variant="info">{detail.permissions.reason}</Alert> : null}
            {detail.overdue ? <Alert variant="error">Das Fälligkeitsdatum ist überschritten.</Alert> : null}
            {detail.duplicates.length > 0 ? (
              <Alert variant="error">
                Mögliche Dublette: dieselbe Rechnungsnummer liegt bereits vor (
                {detail.duplicates.map((item) => item.supplier_name || item.subject || `#${item.candidate_id}`).join(", ")}
                ).
              </Alert>
            ) : null}
            {detail.missing_project ? (
              <Alert variant="info">Keine Projekt- oder Bestellzuordnung und noch keine Ablage.</Alert>
            ) : null}

            <section className="space-y-2">
              <h3 className="font-semibold">Kopfdaten</h3>
              <label className="block text-xs text-label-secondary">
                Lieferant
                <input className={inputClass} value={form.supplier_name} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("supplier_name", event.target.value)} />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs text-label-secondary">
                  Rechnungsnr.
                  <input className={inputClass} value={form.invoice_number} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("invoice_number", event.target.value)} />
                </label>
                <label className="block text-xs text-label-secondary">
                  Rechnungsdatum
                  <input type="date" className={inputClass} value={form.invoice_date} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("invoice_date", event.target.value)} />
                </label>
                <label className="block text-xs text-label-secondary">
                  Netto
                  <input type="number" step="0.01" className={inputClass} value={form.total_net} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("total_net", event.target.value)} />
                </label>
                <label className="block text-xs text-label-secondary">
                  Brutto
                  <input type="number" step="0.01" className={inputClass} value={form.total_gross} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("total_gross", event.target.value)} />
                </label>
                <label className="block text-xs text-label-secondary">
                  Währung
                  <input className={inputClass} value={form.currency} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("currency", event.target.value.toUpperCase())} />
                </label>
                <label className="block text-xs text-label-secondary">
                  Fällig
                  <input type="date" className={inputClass} value={form.due_date} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("due_date", event.target.value)} />
                </label>
              </div>
              <label className="block text-xs text-label-secondary">
                IBAN
                <input className={inputClass} value={form.supplier_iban} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("supplier_iban", event.target.value)} />
              </label>
              <p className="text-xs text-label-tertiary">{formatIban(form.supplier_iban)}</p>
              <label className="block text-xs text-label-secondary">
                Zahlungsbedingung
                <textarea className={inputClass} rows={2} value={form.payment_terms} disabled={!detail.permissions.can_edit_header} onChange={(event) => patch("payment_terms", event.target.value)} />
              </label>
              {detail.header.skonto ? (
                <p className="text-xs font-medium text-status-orange">
                  Skonto {detail.header.skonto.percent} %{detail.header.skonto.until ? ` bis ${formatDay(detail.header.skonto.until)}` : ""}
                </p>
              ) : null}
              <p className="text-xs text-label-tertiary">
                Ab {formatMoney(detail.four_eyes_threshold)} Brutto ist eine zweite Freigabe nötig.
              </p>
              {detail.permissions.can_edit_header ? (
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={busy != null}
                  onClick={() =>
                    void run(
                      "header",
                      () => saveInvoiceApprovalHeader(detail.candidate_id, payloadFromForm(form)),
                      "Kopfdaten gespeichert.",
                    )
                  }
                >
                  Kopfdaten speichern
                </Button>
              ) : null}
            </section>

            {needsSecond ? (
              <label className="block text-xs text-label-secondary">
                Zweite Freigabe durch
                <select className={inputClass} value={secondLogin} onChange={(event) => setSecondLogin(event.target.value)}>
                  <option value="">Person wählen</option>
                  {approvers
                    .filter((person) => person.login !== detail.approval.assignee_login)
                    .map((person) => (
                      <option key={person.login} value={person.login}>
                        {person.name}
                      </option>
                    ))}
                </select>
              </label>
            ) : null}

            <section>
              <h3 className="font-semibold">Projekte und Ablage</h3>
              {detail.projects.length === 0 && detail.filed_paths.length === 0 ? (
                <p className="mt-1 text-label-secondary">Noch keinem Projekt zugeordnet.</p>
              ) : (
                <ul className="mt-1 space-y-1">
                  {detail.projects.map((project) => (
                    <li key={`${project.auftrag_id}-${project.bestellnummer}`}>
                      <span className="font-medium">{project.auftragsnummer || "Projekt"}</span>
                      {project.jahr ? ` · ${project.jahr}` : ""}
                      {project.beschreibung ? ` · ${project.beschreibung}` : ""}
                      {project.bestellnummer ? (
                        <span className="mt-0.5 block font-mono text-xs text-label-secondary">{project.bestellnummer}</span>
                      ) : null}
                    </li>
                  ))}
                  {detail.filed_paths.map((file) => (
                    <li key={`${file.path}-${file.filename}`} className="text-xs text-label-secondary">
                      {file.web_url ? (
                        <a href={file.web_url} target="_blank" rel="noreferrer" className="text-accent hover:underline">
                          {file.path || file.filename || "SharePoint"}
                        </a>
                      ) : (
                        file.path || file.filename
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="font-semibold">
                Positionen
                {detail.line_count > 0 ? ` (${detail.line_count})` : ""}
              </h3>
              {detail.lines.length === 0 ? (
                <p className="mt-1 text-label-secondary">Keine E-Rechnung erkannt. Beträge bitte von Hand prüfen.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {detail.lines.map((line) => {
                    const state = line.price.state;
                    const tone =
                      state === "higher"
                        ? "border-status-red/40 bg-status-red/10"
                        : state === "lower"
                          ? "border-status-orange/40 bg-status-orange/10"
                          : state === "match"
                            ? "border-status-green/30"
                            : "border-separator";
                    return (
                      <li key={line.id} className={`rounded border px-2 py-1.5 ${tone}`}>
                        <div className="font-medium">{line.bezeichnung}</div>
                        <div className="text-xs text-label-secondary">
                          {line.menge ?? "—"} {line.einheit || ""} · {formatMoney(line.einzelpreis_netto, currency)}
                          {line.seller_article_nr ? ` · Art. ${line.seller_article_nr}` : ""}
                        </div>
                        <div className="text-xs">
                          {line.stamm_artikel_id ? (
                            <span>
                              Stamm {line.stamm_nr || line.stamm_artikel_id}
                              {line.stamm_name ? ` · ${line.stamm_name}` : ""} · {matchMethodLabel(line.match_method)}
                              {line.price.stamm_ek != null ? ` · EK ${formatMoney(line.price.stamm_ek, currency)}` : ""}
                            </span>
                          ) : (
                            <span>Nicht in der Artikeldatenbank</span>
                          )}
                          <span className="ml-1 font-semibold">{priceLabel(state, line.price.percent)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
              {detail.unmatched_count > 0 ? (
                <p className="mt-1 text-xs text-label-secondary">
                  {detail.unmatched_count} Position{detail.unmatched_count === 1 ? "" : "en"} ohne Stammartikel.
                </p>
              ) : null}
            </section>

            {detail.permissions.can_decide ? (
              <section className="space-y-2">
                {detail.price_deviation_count > 0 ? (
                  <label className="flex items-start gap-2 text-xs">
                    <input type="checkbox" checked={ackDeviation} onChange={(event) => setAckDeviation(event.target.checked)} />
                    Preisabweichungen geprüft ({detail.price_deviation_count})
                  </label>
                ) : null}
                {detail.duplicates.length > 0 ? (
                  <label className="flex items-start gap-2 text-xs">
                    <input type="checkbox" checked={ackDuplicate} onChange={(event) => setAckDuplicate(event.target.checked)} />
                    Dublette geprüft
                  </label>
                ) : null}
                {detail.missing_project ? (
                  <label className="flex items-start gap-2 text-xs">
                    <input type="checkbox" checked={ackProject} onChange={(event) => setAckProject(event.target.checked)} />
                    Ohne Projektzuordnung freigeben
                  </label>
                ) : null}
                <label className="block text-xs text-label-secondary">
                  Begründung (Pflicht bei Klärung und Ablehnung)
                  <textarea className={inputClass} rows={3} value={note} onChange={(event) => setNote(event.target.value)} />
                </label>
                {detail.permissions.can_comment ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy != null || note.trim().length < 3}
                    onClick={() =>
                      void run(
                        "note",
                        () => decideInvoiceApproval(detail.candidate_id, { action: "kommentar", note }),
                        "Kommentar gespeichert.",
                      )
                    }
                  >
                    Nur Kommentar
                  </Button>
                ) : null}
              </section>
            ) : null}

            <section>
              <h3 className="font-semibold">Protokoll</h3>
              {detail.events.length === 0 ? (
                <p className="mt-1 text-label-secondary">Noch keine Einträge.</p>
              ) : (
                <ol className="mt-1 space-y-1 text-xs text-label-secondary">
                  {detail.events.map((event, index) => (
                    <li key={`${event.created_at}-${index}`}>
                      <span className="text-label">{eventActionLabel(event.action)}</span>
                      {" · "}
                      {event.actor_name || event.actor_login || "—"}
                      {event.created_at ? ` · ${formatDay(event.created_at)}` : ""}
                      {event.note ? <span className="block">{event.note}</span> : null}
                    </li>
                  ))}
                </ol>
              )}
            </section>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}
